import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, Alert, Badge, Button, Group, Modal, Stack, Text, TextInput,
} from '@mantine/core';
import { useAppContext } from '../contexts/AppContext';
import markdownNaarHtml from '../utils/markdown';

const RAPPORT_STATUS_KLEUR = { definitief: 'green', concept: 'gray', vervangen: 'orange' };

// Gereedheidscheck + verificatievragen tijdelijk uit beeld (besluit
// 2026-07-05: te warrig vóór de eerste echte belangentafel; backend blijft
// intact — deze vlag omzetten haalt alles terug).
const TOON_GEREEDHEID = false;

const STATUS_KLEUR = { gedekt: 'green', twijfelachtig: 'yellow', ontbreekt: 'red' };

// Gereedheidspaneel (fase 4): stoplicht per station of de rapportage gevuld
// kan worden, plus de verificatievragen van Claude — direct beantwoordbaar
// of te agenderen voor de volgende belangentafel.
export default function GereedheidPaneel({ buurtcode, buurtnaam }) {
  const { fetchWithAuth, addToast } = useAppContext();
  const [gereedheid, setGereedheid] = useState([]);
  const [rapportages, setRapportages] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [antwoorden, setAntwoorden] = useState({});
  const [rapportInhoud, setRapportInhoud] = useState(null);
  const [kaartbeelden, setKaartbeelden] = useState([]);

  const laad = useCallback(async () => {
    try {
      const [resG, resR] = await Promise.all([
        fetchWithAuth(`/api/buurten/${encodeURIComponent(buurtcode)}/gereedheid`),
        fetchWithAuth(`/api/buurten/${encodeURIComponent(buurtcode)}/rapportages`),
      ]);
      const dataG = await resG.json();
      const dataR = await resR.json();
      if (dataG.success) setGereedheid(dataG.gereedheid);
      if (dataR.success) setRapportages(dataR.rapportages);
    } catch (e) {
      console.warn('Gereedheid laden mislukt:', e);
    }
  }, [buurtcode, fetchWithAuth]);

  useEffect(() => { laad(); }, [laad]);

  const voerCheckUit = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(`/api/buurten/${encodeURIComponent(buurtcode)}/gereedheidscheck`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        addToast(`Check klaar: ${data.onderdelen} onderdelen, ${data.vragen} open vragen`, 'success');
        laad();
      } else {
        addToast(data.error || 'Gereedheidscheck mislukt', 'error');
      }
    } catch (e) {
      addToast('Gereedheidscheck mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  const genereerRapportage = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(`/api/buurten/${encodeURIComponent(buurtcode)}/rapportage`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        addToast(`Concept-rapportage versie ${data.versie} gegenereerd`, 'success');
        laad();
      } else {
        addToast(data.error || 'Rapportage genereren mislukt', 'error');
      }
    } catch (e) {
      addToast('Rapportage genereren mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  const beantwoord = async (vraagId, status) => {
    const body = status === 'geagendeerd'
      ? { status: 'geagendeerd' }
      : { antwoord: antwoorden[vraagId] };
    if (!status && !body.antwoord) return;
    try {
      const res = await fetchWithAuth(`/api/verificatie_vragen/${vraagId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) laad();
      else addToast(data.error || 'Opslaan mislukt', 'error');
    } catch (e) {
      addToast('Opslaan mislukt', 'error');
    }
  };

  const toonRapport = async (id) => {
    const res = await fetchWithAuth(`/api/rapportages/${id}`);
    const data = await res.json();
    if (!data.success) return;
    setRapportInhoud(data.rapportage);
    // Kaartbeelden van stations met geplaatste locaties erbij laden
    // (afbeeldingen vergen een auth-header, dus via fetch naar blob-URL's)
    try {
      const resP = await fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(buurtcode)}`);
      const dataP = await resP.json();
      const stations = (dataP.features || [])
        .filter(f => (f.properties.type || '').toLowerCase().includes('station')
          && f.properties.soort_project === 'UITVOERING'
          && (f.properties.lokatie1 || f.properties.coordinaten))
        .map(f => f.properties.projectnummer);
      const beelden = [];
      for (const pnr of stations) {
        try {
          const resB = await fetchWithAuth(`/api/projecten/${encodeURIComponent(pnr)}/kaartbeeld`);
          if (resB.ok) {
            beelden.push({ pnr, url: URL.createObjectURL(await resB.blob()) });
          }
        } catch (e) { /* station zonder beeld overslaan */ }
      }
      setKaartbeelden(beelden);
    } catch (e) {
      setKaartbeelden([]);
    }
  };

  const sluitRapport = () => {
    kaartbeelden.forEach(b => URL.revokeObjectURL(b.url));
    setKaartbeelden([]);
    setRapportInhoud(null);
  };

  const maakDefinitief = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/rapportages/${id}/definitief`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        addToast('Rapportage is definitief; de vorige definitieve versie is vervangen', 'success');
        sluitRapport();
        laad();
      } else {
        addToast(data.error || 'Definitief maken mislukt', 'error');
      }
    } catch (e) {
      addToast('Definitief maken mislukt', 'error');
    }
  };

  const downloadWord = async (rapport) => {
    try {
      const res = await fetchWithAuth(`/api/rapportages/${rapport.id}/word`);
      if (!res.ok) throw new Error('download mislukt');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lokatierapportage_${buurtcode}_v${rapport.versie}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      addToast('Word-download mislukt', 'error');
    }
  };

  // Groepeer per station (null = buurtbreed)
  const perStation = {};
  gereedheid.forEach(r => {
    const key = r.projectnummer || 'Buurtbreed';
    (perStation[key] = perStation[key] || []).push(r);
  });
  const openVragen = gereedheid.filter(r => r.vraag_id && r.vraag_status === 'open');

  return (
    <Stack gap={6}>
      {/* De kop zit in de 'Rapportages'-dropdown van de buurt-zijbalk */}
      <Text size="xs" c="dimmed">
        Genereert een concept-lokatierapportage uit het gecontroleerde
        transcript en de vastgelegde gegevens.
      </Text>
      <Group gap="xs">
        {TOON_GEREEDHEID && (
          <Button size="compact-xs" variant="default" onClick={voerCheckUit} loading={bezig}>
            Check uitvoeren
          </Button>
        )}
        <Button size="compact-xs" color="groen" onClick={genereerRapportage} loading={bezig}>
          Concept-rapportage
        </Button>
      </Group>

      {TOON_GEREEDHEID && Object.keys(perStation).length > 0 && (
        <Accordion variant="contained" chevronPosition="left">
          {Object.entries(perStation).map(([station, rijen]) => {
            const telling = { gedekt: 0, twijfelachtig: 0, ontbreekt: 0 };
            rijen.forEach(r => { telling[r.status] = (telling[r.status] || 0) + 1; });
            return (
              <Accordion.Item key={station} value={station}>
                <Accordion.Control>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm">{station}</Text>
                    {Object.entries(telling).filter(([, n]) => n > 0).map(([st, n]) => (
                      <Badge key={st} color={STATUS_KLEUR[st]} variant="light" size="xs">{n}</Badge>
                    ))}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {rijen.map(r => (
                      <Group key={r.id + '-' + (r.vraag_id || 0)} gap={6} wrap="nowrap" align="flex-start">
                        <Badge color={STATUS_KLEUR[r.status] || 'gray'} variant="filled" size="xs"
                          style={{ flexShrink: 0, marginTop: 3 }}>&nbsp;</Badge>
                        <Stack gap={2} style={{ flex: 1 }}>
                          <Text size="xs" fw={500}>{r.onderdeel}</Text>
                          {r.toelichting && <Text size="xs" c="dimmed">{r.toelichting}</Text>}
                          {r.vraag_id && r.vraag_status !== 'open' && (
                            <Text size="xs" c="dimmed">
                              {r.vraag_status === 'beantwoord'
                                ? `Beantwoord: ${r.antwoord}`
                                : 'Geagendeerd voor volgende belangentafel'}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {TOON_GEREEDHEID && openVragen.length > 0 && (
        <Alert color="yellow" title={`${openVragen.length} open verificatievragen`} p="xs">
          <Stack gap={6}>
            {openVragen.map(v => (
              <Stack key={v.vraag_id} gap={4}>
                <Text size="xs">{v.vraag}</Text>
                <Group gap={4} wrap="nowrap">
                  <TextInput
                    size="xs" placeholder="Antwoord..." style={{ flex: 1 }}
                    value={antwoorden[v.vraag_id] || ''}
                    onChange={e => { const w = e.currentTarget.value; setAntwoorden(prev => ({ ...prev, [v.vraag_id]: w })); }}
                  />
                  <Button size="compact-xs" onClick={() => beantwoord(v.vraag_id)}
                    disabled={!antwoorden[v.vraag_id]}>Opslaan</Button>
                  <Button size="compact-xs" variant="default"
                    onClick={() => beantwoord(v.vraag_id, 'geagendeerd')}>Agenderen</Button>
                </Group>
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}

      {rapportages.length > 0 && (
        <Stack gap={2}>
          <Text fw={600} size="sm">Eerder gegenereerd</Text>
          {rapportages.map(r => (
            <Group key={r.id} justify="space-between" wrap="nowrap">
              <Text size="xs">
                Versie {r.versie} — {r.gegenereerd_op ? new Date(r.gegenereerd_op).toLocaleDateString('nl-NL') : ''}
              </Text>
              <Group gap={4}>
                <Badge size="xs" variant="light" color={RAPPORT_STATUS_KLEUR[r.status] || 'gray'}>
                  {r.status}
                </Badge>
                <Button size="compact-xs" variant="default" onClick={() => toonRapport(r.id)}>Bekijk</Button>
                <Button size="compact-xs" variant="default" onClick={() => downloadWord(r)}>Word</Button>
              </Group>
            </Group>
          ))}
        </Stack>
      )}

      <Modal
        opened={!!rapportInhoud} onClose={sluitRapport}
        fullScreen transitionProps={{ duration: 0 }}
        title={rapportInhoud ? (
          <Group gap="sm">
            <Text fw={700}>Lokatierapportage {buurtnaam || buurtcode} — versie {rapportInhoud.versie}</Text>
            <Badge variant="light" color={RAPPORT_STATUS_KLEUR[rapportInhoud.status] || 'gray'}>
              {rapportInhoud.status}
            </Badge>
          </Group>
        ) : ''}
      >
        {rapportInhoud && (
          <Stack gap="md" align="center">
            <Group gap="xs">
              <Button size="xs" variant="default"
                onClick={() => downloadWord(rapportInhoud)}>
                Download als Word
              </Button>
              {rapportInhoud.status === 'concept' && (
                <Button size="xs" color="groen"
                  onClick={() => maakDefinitief(rapportInhoud.id)}>
                  Definitief maken
                </Button>
              )}
            </Group>
            <div
              className="rapport-weergave"
              style={{
                maxWidth: 820, width: '100%', background: '#fff',
                padding: '2rem 2.5rem', lineHeight: 1.55, fontSize: 15,
              }}
              dangerouslySetInnerHTML={{ __html: markdownNaarHtml(rapportInhoud.inhoud) }}
            />
            {kaartbeelden.length > 0 && (
              <div className="rapport-weergave" style={{
                maxWidth: 820, width: '100%', background: '#fff',
                padding: '1rem 2.5rem 2rem', lineHeight: 1.55, fontSize: 15,
              }}>
                <h2>Kaartbeelden locatiekeuzes</h2>
                <p>Groen met ster = gekozen locatie; grijs = afgevallen alternatief.</p>
                {kaartbeelden.map(b => (
                  <div key={b.pnr} style={{ marginBottom: '1.2rem' }}>
                    <h3>Station {b.pnr}</h3>
                    <img src={b.url} alt={`Locatiekeuzes ${b.pnr}`}
                      style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 6 }} />
                  </div>
                ))}
              </div>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
